import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowForward as ArrowForwardIcon,
  HomeWorkOutlined as HomeWorkOutlinedIcon,
  LocalFloristOutlined as LocalFloristOutlinedIcon,
  SupportAgentOutlined as SupportAgentOutlinedIcon,
} from '@mui/icons-material';
import { useSection3Image } from '../../hooks/useSection3Image';
import { useRevealOnScroll } from '../../hooks/useRevealOnScroll';

interface WhyBuyNewSectionProps {
  id?: string;
  className?: string;
}

const HIGHLIGHTS = [
  {
    icon: HomeWorkOutlinedIcon,
    title: 'Une expertise reconnue',
    description:
      "Une équipe expérimentée et des compétences complémentaires pour mener vos projets de l'idée à la livraison.",
  },
  {
    icon: LocalFloristOutlinedIcon,
    title: 'Des projets durables',
    description:
      'Nous intégrons les enjeux environnementaux et sociétaux dans chacune de nos réalisations.',
  },
  {
    icon: SupportAgentOutlinedIcon,
    title: 'Un accompagnement sur-mesure',
    description:
      'Proximité, transparence et réactivité à chaque étape de votre projet.',
  },
] as const;

const WhyBuyNewSection: React.FC<WhyBuyNewSectionProps> = ({ id, className = '' }) => {
  const { ref: headerRef, visible: headerVisible } = useRevealOnScroll<HTMLDivElement>();
  const { image: backgroundImage } = useSection3Image();

  return (
    <section
      {...(id ? { id } : {})}
      className={`relative overflow-hidden bg-brand-charcoal py-14 md:py-16 lg:py-20 ${
        id ? 'scroll-mt-28 md:scroll-mt-32' : ''
      } ${className}`}
    >
      {/* Fond graphique Section 3 */}
      <img
        src={backgroundImage}
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full scale-[1.12] object-cover object-center"
        aria-hidden
        loading="lazy"
      />

      <div className="relative z-[1] mx-auto w-full max-w-[1440px] px-4 sm:px-5 md:px-6">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-2 lg:gap-0">
          {/* Colonne gauche — présentation */}
          <div
            ref={headerRef}
            className={`reveal-up lg:border-r lg:border-neutral-600/45 lg:pr-10 xl:pr-14 ${
              headerVisible ? 'reveal-up--visible' : ''
            }`}
          >
            <p className="eyebrow-accent mb-4 text-[11px] tracking-[0.18em]">02 • À propos de Proméa</p>
            <div className="accent-dash mb-6" aria-hidden />
            <h2 className="headline-brand-section max-w-xl text-white">
              Donner forme à des projets qui ont{' '}
              <span className="headline-brand-accent">du sens.</span>
            </h2>
            <p className="mt-6 max-w-lg text-[15px] font-normal leading-[1.75] text-white sm:text-base lg:mt-7 lg:text-[17px]">
              Proméa est spécialiste de la promotion immobilière et de
              l&apos;aménagement. Nous concevons et réalisons des projets durables, à taille humaine,
              en plaçant la qualité, l&apos;écoute et la confiance au cœur de notre engagement.
            </p>
            <Link
              to="/nous-decouvrir"
              className="group mt-9 inline-flex min-h-[3rem] items-center justify-center gap-2 rounded-md border border-orange-500 px-7 py-3 text-[14px] font-medium text-orange-500 transition-[background-color,transform,box-shadow,color] duration-normal hover:bg-orange-500/10 hover:shadow-[0_0_20px_rgba(223,130,47,0.15)] active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/50 focus-visible:ring-offset-2 focus-visible:ring-offset-brand-charcoal sm:mt-10 sm:px-8 sm:text-[15px]"
            >
              En savoir plus
              <ArrowForwardIcon
                className="h-[18px] w-[18px] text-orange-500 transition-transform duration-normal group-hover:translate-x-0.5"
                aria-hidden
              />
            </Link>
          </div>

          {/* Colonne droite — points clés */}
          <ul className="flex flex-col gap-9 lg:pl-10 xl:pl-14">
            {HIGHLIGHTS.map(({ icon: Icon, title, description }, index) => (
              <li
                key={title}
                className={`reveal-up flex gap-4 sm:gap-5 ${
                  headerVisible ? 'reveal-up--visible' : ''
                }`}
                style={{ transitionDelay: `${(index + 1) * 120}ms` }}
              >
                <div
                  className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/30 sm:h-[3.75rem] sm:w-[3.75rem]"
                  aria-hidden
                >
                  <Icon className="h-6 w-6 text-white sm:h-7 sm:w-7" />
                </div>
                <div className="min-w-0 pt-0.5">
                  <h3 className="text-base font-semibold text-white sm:text-lg">{title}</h3>
                  <p className="mt-2 text-[14px] font-normal leading-[1.65] text-white/75 sm:text-[15px]">
                    {description}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default WhyBuyNewSection;
